/** Lobby row config — games filtered client-side from API pool (UI only). */
export type LotusLobbyGroupConfig = {
  id: string
  title: string
  /** Apollo icon class for lobby row title (optional) */
  iconClass?: string
  /** Header tab route `/casino-int/tab/N` for "More Games" */
  tabIndex: number
  /** Lotus `groupID` query for shared tab routes (e.g. Recommended → tab/15?groupID=0) */
  groupId?: number
  maxGames: number
  useTopProviders?: boolean
  providerCategories?: string[]
  patterns?: RegExp[]
  /** Games matching these patterns are excluded after provider filter */
  excludePatterns?: RegExp[]
}

export const LOTUS_LOBBY_GROUPS: LotusLobbyGroupConfig[] = [
  {
    id: 'recommended',
    title: 'Recommended',
    iconClass: 'icon-recommended-new',
    tabIndex: 15,
    groupId: 0,
    maxGames: 9,
    useTopProviders: true,
  },
  {
    id: 'trending',
    title: 'Trending Now',
    iconClass: 'icon-fire',
    tabIndex: 15,
    groupId: 1,
    maxGames: 9,
    patterns: [/trending|hot|fire|aviator|crash|mines|plinko|spribe|jetx|balloon/i],
  },
  {
    id: 'editors-choice',
    title: "Editor's Choice",
    iconClass: 'icon-popular-new',
    tabIndex: 15,
    groupId: 2,
    maxGames: 9,
    patterns: [/premium|gold|vip|classic|mega|fortune|treasure|royal/i],
  },
  {
    id: 'evolution',
    title: 'Evolution',
    tabIndex: 3,
    maxGames: 9,
    providerCategories: ['Evolution Live'],
    patterns: [
      /lightning\s*roulette|lightning\s*baccarat|lightning\s*blackjack|lightning\s*dice|lightning\s*ball|dream\s*catcher|crazy\s*time|monopoly|mega\s*ball|deal\s*or\s*no\s*deal|funky\s*time|stock\s*market|gonzo|cash\s*or\s*crash|immersive|infinite|speed\s*roulette|speed\s*baccarat|speed\s*blackjack|power\s*blackjack|xxxtreme|adventures?\s*beyond|first\s*person|live\s*roulette|live\s*blackjack|live\s*baccarat|live\s*poker|live\s*lobby/i,
    ],
  },
  {
    id: 'indian-games',
    title: 'Indian Games',
    tabIndex: 4,
    maxGames: 9,
    providerCategories: ['Mac88', 'Mac88 Gaming'],
    patterns: [/teen.?patti|andar.?bahar|indian|32.?cards|teenpatti|ak47|muflis|joker|lucky\s*7|7\s*up|matka|bollywood/i],
  },
  {
    id: 'teen-patti',
    title: 'Teen Patti',
    tabIndex: 5,
    maxGames: 9,
    providerCategories: ['Ezugi', 'Evolution Live', 'KM', 'Sexy', 'Kingmidas', '100hp'],
    patterns: [
      /teen\s*patti|teenpatti|3\s*patti|trio|muflis|ak\s*47|ak47|2000\s*ad|32\s*cards|card\s*32/i,
    ],
    excludePatterns: [/poker|blackjack|roulette|baccarat|dragon\s*tiger|sic\s*bo|andar\s*bahar/i],
  },
  {
    id: 'roulette',
    title: 'Roulette',
    tabIndex: 6,
    maxGames: 9,
    providerCategories: ['Evolution Live', 'Ezugi', 'Playtech'],
    patterns: [/roulette|roleta|rulet|mega\s*fire/i],
    excludePatterns: [/blackjack|baccarat|poker|dragon\s*tiger|sic\s*bo|teen\s*patti|game\s*show|crazy\s*time|monopoly/i],
  },
  {
    id: 'baccarat',
    title: 'Baccarat',
    tabIndex: 7,
    maxGames: 9,
    providerCategories: ['Evolution Live', 'Ezugi'],
    patterns: [/baccarat|bacara|punto\s*blanco/i],
    excludePatterns: [/roulette|blackjack|poker|dragon\s*tiger|sic\s*bo|teen\s*patti|game\s*show|crazy\s*time/i],
  },
  {
    id: 'instant-games',
    title: 'Instant Games',
    tabIndex: 8,
    maxGames: 9,
    providerCategories: ['Spribe', '100hp', 'Smartsoft'],
    patterns: [/instant|crash|aviator|mines|plinko|limbo|keno|hilo|jetx|balloon|spaceman|turbo/i],
    excludePatterns: [/roulette|blackjack|baccarat|poker|dragon\s*tiger|sic\s*bo|teen\s*patti|live/i],
  },
  {
    id: 'game-shows',
    title: 'Game Shows',
    tabIndex: 9,
    maxGames: 9,
    providerCategories: ['Evolution Live'],
    patterns: [
      /game\s*show|crazy\s*time|monopoly|dream\s*catcher|deal\s*or\s*no\s*deal|mega\s*ball|cash\s*or\s*crash|funky\s*time|lightning\s*storm|sweet\s*bonanza\s*candyland|stock\s*market|xxxtreme|adventures?\s*beyond|gonzo\s*treasure|spin\s*a\s*win|extra\s*chilli/i,
    ],
    excludePatterns: [/roulette|blackjack|baccarat|poker|dragon\s*tiger|sic\s*bo|teen\s*patti|speed\s*roulette|speed\s*baccarat/i],
  },
  {
    id: 'dragon-tiger',
    title: 'Dragon Tiger',
    tabIndex: 10,
    maxGames: 9,
    providerCategories: ['Ezugi', 'Evolution Live', 'Sexy', 'KM'],
    patterns: [/dragon\s*tiger|dragontiger|long\s*hu|longhu|dragon\s*&\s*tiger/i],
    excludePatterns: [/roulette|blackjack|baccarat|poker|sic\s*bo|teen\s*patti|game\s*show|crazy\s*time/i],
  },
  {
    id: 'blackjack',
    title: 'Blackjack',
    tabIndex: 11,
    maxGames: 9,
    providerCategories: ['Evolution Live', 'Ezugi', 'Playtech'],
    patterns: [/blackjack|black\s*jack|\b21\b|infinite\s*blackjack|power\s*blackjack|speed\s*blackjack/i],
    excludePatterns: [/roulette|baccarat|poker|dragon\s*tiger|sic\s*bo|teen\s*patti|game\s*show|crazy\s*time/i],
  },
  {
    id: 'poker',
    title: 'Poker',
    tabIndex: 12,
    maxGames: 9,
    providerCategories: ['Evolution Live', 'Ezugi', 'Playtech'],
    patterns: [
      /poker|hold\s*'?em|holdem|texas\s*hold|caribbean\s*stud|omaha|casino\s*hold\s*'?em|2\s*hand\s*casino|triple\s*card\s*poker|2\s*hand\s*poker|ultimate\s*texas/i,
    ],
    excludePatterns: [/roulette|blackjack|baccarat|dragon\s*tiger|sic\s*bo|game\s*show|crazy\s*time|monopoly|aviator|crash|mines/i],
  },
  {
    id: 'slots',
    title: 'Slots',
    tabIndex: 13,
    maxGames: 9,
    providerCategories: ['100hp', 'JILI', 'PG Soft', 'Pragmatic'],
    patterns: [/slot|reel|fruit|gems|bonanza|olympus|sweet\s*bonanza|candy|wild|megaways|book\s*of/i],
    excludePatterns: [/roulette|blackjack|baccarat|poker|dragon\s*tiger|sic\s*bo|teen\s*patti|game\s*show|live|hold\s*'?em|andar\s*bahar/i],
  },
  {
    id: 'sicbo',
    title: 'Sicbo',
    tabIndex: 14,
    maxGames: 9,
    providerCategories: ['Ezugi', 'Evolution Live', 'Playtech'],
    patterns: [/sic\s*bo|sicbo|super\s*dice|tai\s*sai|fan\s*tan|fantan/i],
    excludePatterns: [/roulette|blackjack|baccarat|poker|dragon\s*tiger|teen\s*patti|game\s*show|crazy\s*time/i],
  },
  {
    id: 'sport-games',
    title: 'Sport Games',
    tabIndex: 17,
    maxGames: 9,
    providerCategories: ['100hp', 'Spribe', 'Smartsoft', 'Turbogames', 'JILI'],
    patterns: [
      /sport\s*game|virtual\s*sport|football|soccer|cricket|penalty|shootout|goal|horse\s*race|horse\s*racing|basketball|tennis|darts|archery|hockey|baseball|matchday|league\s*of/i,
    ],
    excludePatterns: [/roulette|blackjack|baccarat|poker|dragon\s*tiger|sic\s*bo|teen\s*patti|game\s*show|crazy\s*time|aviator|mines|plinko/i],
  },
]

/** Primary lobby row shown when opening a header tab page via "More Games". */
export function getLobbyGroupForTab(tabIndex: number): LotusLobbyGroupConfig | undefined {
  return LOTUS_LOBBY_GROUPS.find((g) => g.tabIndex === tabIndex)
}

export function getLobbyGroupByGroupId(groupId: number): LotusLobbyGroupConfig | undefined {
  return LOTUS_LOBBY_GROUPS[groupId]
}

export function getLotusLobbyMoreGamesPath(group: LotusLobbyGroupConfig): string {
  const base = `/casino-int/tab/${group.tabIndex}`
  if (group.groupId != null) {
    return `${base}?groupID=${group.groupId}`
  }
  return base
}

const EVOLUTION_TAB_ROW_IDS = [
  'roulette',
  'baccarat',
  'blackjack',
  'game-shows',
  'poker',
  'dragon-tiger',
  'sicbo',
] as const

/** Evolution tab — lobby-style subcategory rows (baccarat, roulette, etc.) */
export function getEvolutionTabLobbyRows(): LotusLobbyGroupConfig[] {
  const rows: LotusLobbyGroupConfig[] = []
  for (const id of EVOLUTION_TAB_ROW_IDS) {
    const group = LOTUS_LOBBY_GROUPS.find((g) => g.id === id)
    if (!group) continue
    rows.push({
      ...group,
      tabIndex: 3,
      maxGames: 60,
      providerCategories: ['Evolution Live'],
    })
  }
  return rows
}

/** Indian Games tab (Mac88) — same grouped layout as Evolution, Mac88 provider only. */
const INDIAN_GAMES_TAB_ROW_IDS = [
  'teen-patti',
  'dragon-tiger',
  'roulette',
  'baccarat',
  'poker',
  'sicbo',
] as const

export const MAC88_PROVIDER_CATEGORIES = ['Mac88', 'Mac88 Gaming'] as const

export function getIndianGamesTabLobbyRows(): LotusLobbyGroupConfig[] {
  const rows: LotusLobbyGroupConfig[] = []
  for (const id of INDIAN_GAMES_TAB_ROW_IDS) {
    const group = LOTUS_LOBBY_GROUPS.find((g) => g.id === id)
    if (!group) continue
    rows.push({
      ...group,
      tabIndex: 4,
      maxGames: 60,
      providerCategories: [...MAC88_PROVIDER_CATEGORIES],
    })
  }
  return rows
}
